/**
 * Observes form section elements and reports the one currently in view so the
 * section navigation can stay in sync while scrolling.
 *
 * @module medicalPaForm/hooks/useSectionScrollObserver
 */
import { useEffect } from "react";

/**
 * Attaches an {@link IntersectionObserver} to every `.single-section__container`
 * and invokes `scrollSectionId` with the id of the section that is >90% visible.
 * Re-attaches whenever `refreshKey` changes (e.g. when the form config resolves).
 *
 * @param scrollSectionId - Callback invoked with the visible section's id.
 * @param refreshKey - Value that, when changed, re-attaches the observer.
 */
const useSectionScrollObserver = (
  scrollSectionId: ((sectionKey: string) => void) | undefined,
  refreshKey: unknown,
): void => {
  useEffect(() => {
    const observedSections = document.querySelectorAll(
      ".single-section__container",
    );

    const root = document.querySelector(
      ".main-form.medical-pharma-pa-form__body",
    );

    if (observedSections.length === 0) return;
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0.9) {
          scrollSectionId?.(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: root,
      threshold: [0.5, 0.9],
    });

    observedSections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [refreshKey]);
};

export default useSectionScrollObserver;
